// external
import { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// tools
import { dqfOperations } from '../../../../../../../../ducks/dqf';
import { alertsOperations } from '../../../../../../../../ducks/alerts';
import { hooks } from './hooks';
import { exportCombinedExcelWorkbooks } from '../../../../../../../common/Kendo/ExcelExport';
// components
import {
  MisReportHeaderCard,
  AlcoholTestingTable,
  DrugTestingTable
} from './components';
import { ButtonGroups, ButtonGroup, Button } from '../../../../../../../../components/common/Kendo/Button';
import { Tooltip } from '../../../../../../../../components/common/Kendo/Tooltip';

/**
 * ManagementInformationSystems page component.
 * Displays MIS report controls and tables for Drug and Alcohol Testing.
 * Handles site selection, DOT/year selection, and toggling child locations.
 * Table view is controlled by user actions on the view changer button group.
 *
 * @returns {JSX.Element} rendered Management Information Systems Report page.
 */
export const ManagementInformationSystems = () => {
  const drugExcelExportRef = useRef(null);
  const alcoholExcelExportRef = useRef(null);
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth?.user)
  const currentUserId = currentUser?._id; 
  const userSites = currentUser?.sites;
  const selectedSite = useSelector((state) => state.dqf?.sites?.selectedSites[0]);

  const selectedSiteData = useMemo(() => {
    if (!selectedSite || !userSites) return null;
    return userSites.find(site => site._id === selectedSite._id) || null;
  }, [selectedSite, userSites]);

  const legacyClientId = selectedSiteData?.legacyClientId;
  const [misReportData, setMisReportData] = useState(null);
  const [activeTab, setActiveTab] = useState('both'); // table view state

  useEffect(() => {
    dispatch(dqfOperations.setSitesSelectorEnabled(true));
  }, [dispatch]);

  // fetches DOT list and testing authorities based on selected sites and current user ID and sets selected DOT Number & year to null initially
  const {
    dotList,
    selectedDOT,
    setSelectedDOT,
    selectedYear,
    setSelectedYear,
    testingAuthorityOptions,
    setSelectedTestingAuthority,
    selectedTestingAuthority,
  } = hooks.useDotList(legacyClientId, currentUserId);

  // exports Excel references for drug and alcohol tables or alerts the User if not possible
  const canExportIndividualTables = false; // set to true to allow individual table exports
  const handleCombinedExport = async () => {
    try {
      await exportCombinedExcelWorkbooks({
        workbookRefs: [drugExcelExportRef, alcoholExcelExportRef],
        sheetNames: ['Drug Testing', 'Alcohol Testing'],
        fileName: `${selectedYear.value} ${selectedSite?.name} MIS Report  - DOT ${selectedDOT.value} for ${selectedTestingAuthority?.label}.xlsx`,
      });
    } catch (err) {
      dispatch(alertsOperations.addAlert(err.message, 'danger'));
    }
  };

  const tableViewButtons = [
    { content: 'Drug Testing', view: 'drugTesting' },
    { content: 'Alcohol Testing', view: 'alcoholTesting' },
    { content: 'Both', view: 'both' },
  ];

  return (
    <div className="management-information-systems-container">
      <MisReportHeaderCard
        dotList={dotList}
        selectedDOT={selectedDOT}
        setSelectedDOT={setSelectedDOT}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        setMisReportData={setMisReportData}
        selectedSite={selectedSite}
        testingAuthorityOptions={testingAuthorityOptions}
        selectedTestingAuthority={selectedTestingAuthority}
        setSelectedTestingAuthority={setSelectedTestingAuthority}
      />
      {misReportData ? (
        <>
          <hr />
          <div className="bg-white p-3">
            <div className="export-to-excel d-flex justify-content-end my-3">
              <Tooltip 
                content={
                <span style={{ maxWidth: 220, whiteSpace: 'normal', display: 'block', textAlign: 'center' }}>
                  Export the combined Drug and Alcohol Testing tables to a single Excel file.
                </span>
                } 
                position="left"
                anchorElement="target"
                >
                <Button
                  content="Export Combined Report"
                  title="Export Combined Report"
                  onClick={handleCombinedExport}
                  disabled={!misReportData || !selectedDOT || !selectedYear}
                  themeColor='info'
                />
              </Tooltip>
            </div>
            <div className="change-table-view d-flex justify-content-end">
              <div className='my-auto mr-2'>View: </div>
              <ButtonGroup>
                <ButtonGroups.ViewChanger
                  buttons={tableViewButtons}
                  currentView={activeTab}
                  setView={setActiveTab}
                  themeColor='info'
                />
              </ButtonGroup>
            </div>

            <div className="mis-tables bg-white">
              {(activeTab === 'drugTesting' || activeTab === 'both') && (
                <DrugTestingTable
                  drugTestingData={misReportData.drugReporting}
                  excelExportRef={drugExcelExportRef}
                  exportable={canExportIndividualTables}
                />
              )}
              {(activeTab === 'alcoholTesting' || activeTab === 'both') && (
                <div style={{ marginTop: (activeTab === 'both' ? '2rem' : 0) }}>
                  <AlcoholTestingTable
                    alcoholTestingData={misReportData.alcoholReporting}
                    excelExportRef={alcoholExcelExportRef}
                    exportable={canExportIndividualTables}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}